import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";

// Contexts
import { AuthProvider } from "./contexts/AuthContext";

// Pages
import LandingPage from "./pages/Landing";
import LoginPage from "./pages/Login";

// Dashboard Pages
import Overview from "./pages/Dashboard/Overview";
import Queries from "./pages/Dashboard/Queries";
import Actions from "./pages/Dashboard/Actions";
import Reddit from "./pages/Dashboard/Reddit";
import Content from "./pages/Dashboard/Content";
import Competitors from "./pages/Dashboard/Competitors";
import Health from "./pages/Dashboard/Health";
import Reports from "./pages/Dashboard/Reports";
import Settings from "./pages/Dashboard/Settings";
import ApiKeys from "./pages/Dashboard/ApiKeys";
import History from "./pages/Dashboard/History";
import Benchmarks from "./pages/Dashboard/Benchmarks";
import Sources from "./pages/Dashboard/Sources";

// Components
import AuthCallback from "./components/AuthCallback";
import ProtectedRoute from "./components/ProtectedRoute";
import DashboardLayout from "./components/Layout/DashboardLayout";

// Dashboard wrapper with layout
function DashboardRoute({ children }) {
  return (
    <ProtectedRoute>
      <DashboardLayout>
        {children}
      </DashboardLayout>
    </ProtectedRoute>
  );
}

// Auth callback handler - checks for session_id in hash on any dashboard route
function AuthCallbackWrapper({ children }) {
  const location = useLocation();
  const [showAuthCallback, setShowAuthCallback] = React.useState(false);
  
  // Check for session_id on mount and location changes
  React.useEffect(() => {
    const hash = window.location.hash;
    const search = window.location.search;
    const fullUrl = window.location.href;
    
    const hasSessionId = 
      (hash && hash.includes('session_id=')) || 
      (search && search.includes('session_id=')) ||
      (fullUrl && fullUrl.includes('session_id='));
    
    if (hasSessionId) {
      console.log('[AuthCallbackWrapper] Session ID detected, showing AuthCallback');
      setShowAuthCallback(true);
    }
  }, [location]);

  // Callback to hide the AuthCallback after successful auth
  const onAuthComplete = React.useCallback(() => {
    console.log('[AuthCallbackWrapper] Auth complete, hiding AuthCallback');
    setShowAuthCallback(false);
  }, []);

  if (showAuthCallback) {
    return <AuthCallback onAuthComplete={onAuthComplete} />;
  }

  // Check on initial render (before useEffect runs)
  const hash = window.location.hash;
  const search = window.location.search;
  if ((hash && hash.includes('session_id=')) || (search && search.includes('session_id='))) {
    return <AuthCallback onAuthComplete={onAuthComplete} />;
  }

  return children;
}

function AppRoutes() {
  return (
    <AuthCallbackWrapper>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        
        {/* Dashboard Routes */}
        <Route path="/dashboard" element={<DashboardRoute><Overview /></DashboardRoute>} />
        <Route path="/dashboard/queries" element={<DashboardRoute><Queries /></DashboardRoute>} />
        <Route path="/dashboard/actions" element={<DashboardRoute><Actions /></DashboardRoute>} />
        <Route path="/dashboard/reddit" element={<DashboardRoute><Reddit /></DashboardRoute>} />
        <Route path="/dashboard/content" element={<DashboardRoute><Content /></DashboardRoute>} />
        <Route path="/dashboard/competitors" element={<DashboardRoute><Competitors /></DashboardRoute>} />
        <Route path="/dashboard/health" element={<DashboardRoute><Health /></DashboardRoute>} />
        <Route path="/dashboard/reports" element={<DashboardRoute><Reports /></DashboardRoute>} />
        <Route path="/dashboard/settings" element={<DashboardRoute><Settings /></DashboardRoute>} />
        <Route path="/dashboard/api-keys" element={<DashboardRoute><ApiKeys /></DashboardRoute>} />
        <Route path="/dashboard/history" element={<DashboardRoute><History /></DashboardRoute>} />
        <Route path="/dashboard/benchmarks" element={<DashboardRoute><Benchmarks /></DashboardRoute>} />
        <Route path="/dashboard/sources" element={<DashboardRoute><Sources /></DashboardRoute>} />
        
        {/* Fallback */}
        <Route path="*" element={<LandingPage />} />
      </Routes>
    </AuthCallbackWrapper>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
